/**
 * Shared outline bitmap generation used by CanvasEditor and Outline prep preview.
 * Width controls stroke thickness (dilation). Blur is applied separately via CSS.
 *
 * The bitmap is padded by the stroke radius so dilation is not clipped at the
 * subject / image edges (or by a tight opaque crop bounding box in the editor).
 *
 * Opaque islands other than the largest connected component are stripped so
 * faint BG-removal sandbox watermarks / corner speckles are not outlined.
 */

function hexToRgb(hex) {
  const raw = typeof hex === "string" ? hex.trim() : "#000000";
  const normalized = (raw.startsWith("#") ? raw.slice(1) : raw).padEnd(6, "0");
  return {
    r: parseInt(normalized.slice(0, 2), 16) || 0,
    g: parseInt(normalized.slice(2, 4), 16) || 0,
    b: parseInt(normalized.slice(4, 6), 16) || 0,
  };
}

/** Ultra-fast box dilation (separable H + V passes). */
export function dilateAlphaMaskBox(alphaData, width, height, radius) {
  const radiusInt = Math.ceil(radius);
  if (radiusInt === 0) return alphaData;

  const temp = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let maxAlpha = 0;
      const xStart = Math.max(0, x - radiusInt);
      const xEnd = Math.min(width - 1, x + radiusInt);
      for (let xi = xStart; xi <= xEnd; xi++) {
        const val = alphaData[y * width + xi];
        if (val > maxAlpha) maxAlpha = val;
      }
      temp[y * width + x] = maxAlpha;
    }
  }

  const result = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) {
    const yStart = Math.max(0, y - radiusInt);
    const yEnd = Math.min(height - 1, y + radiusInt);
    for (let x = 0; x < width; x++) {
      let maxAlpha = 0;
      for (let yi = yStart; yi <= yEnd; yi++) {
        const val = temp[yi * width + x];
        if (val > maxAlpha) maxAlpha = val;
      }
      result[y * width + x] = maxAlpha;
    }
  }

  return result;
}

/**
 * Keep only the largest 8-connected opaque component; zero the rest.
 * Removes disconnected margin junk (e.g. PhotoRoom sandbox watermark pixels)
 * that would otherwise become outline color patches in corners.
 *
 * @param {Uint8Array} alphaData - binary-ish mask (0 = empty, else opaque)
 * @param {number} width
 * @param {number} height
 * @returns {Uint8Array}
 */
export function keepLargestAlphaComponent(alphaData, width, height) {
  const n = width * height;
  if (!alphaData || n === 0 || alphaData.length < n) {
    return alphaData;
  }

  const labels = new Int32Array(n);
  const sizes = [0];
  let nextLabel = 0;
  const stack = [];

  for (let i = 0; i < n; i++) {
    if (alphaData[i] === 0 || labels[i] !== 0) continue;

    nextLabel += 1;
    let size = 0;
    labels[i] = nextLabel;
    stack.push(i);

    while (stack.length) {
      const idx = stack.pop();
      size += 1;
      const x = idx % width;
      const y = (idx / width) | 0;

      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
          const ni = ny * width + nx;
          if (alphaData[ni] === 0 || labels[ni] !== 0) continue;
          labels[ni] = nextLabel;
          stack.push(ni);
        }
      }
    }

    sizes[nextLabel] = size;
  }

  if (nextLabel <= 1) {
    return alphaData;
  }

  let bestLabel = 1;
  let bestSize = sizes[1] || 0;
  for (let label = 2; label <= nextLabel; label++) {
    if (sizes[label] > bestSize) {
      bestSize = sizes[label];
      bestLabel = label;
    }
  }

  const result = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    if (labels[i] === bestLabel) {
      result[i] = alphaData[i];
    }
  }
  return result;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (
      typeof src === "string" &&
      /^https?:\/\//i.test(src) &&
      !src.startsWith("blob:") &&
      !src.startsWith("data:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image for outline"));
    img.src = src;
  });
}

/**
 * @param {string} imageUrl - cutout image (blob/data preferred)
 * @param {{ width: number, color?: string, alphaThreshold?: number }} options
 * @returns {Promise<{ dataUrl: string, bleedPx: number }|null>}
 *   dataUrl — PNG outline layer (padded by bleed)
 *   bleedPx — padding on each side in *native* image pixels (for editor alignment)
 */
export async function generateOutlineDataUrl(imageUrl, options = {}) {
  const strokeWidth = Math.max(0, Number(options.width) || 0);
  if (!imageUrl || strokeWidth <= 0) {
    return null;
  }

  const img = await loadImage(imageUrl);
  const nativeWidth = img.naturalWidth || img.width;
  const nativeHeight = img.naturalHeight || img.height;
  if (!nativeWidth || !nativeHeight) {
    return null;
  }

  const MAX_OUTLINE_RESOLUTION = 2000;
  let workingWidth = nativeWidth;
  let workingHeight = nativeHeight;
  let scale = 1;

  if (nativeWidth > MAX_OUTLINE_RESOLUTION || nativeHeight > MAX_OUTLINE_RESOLUTION) {
    scale =
      nativeWidth > nativeHeight
        ? MAX_OUTLINE_RESOLUTION / nativeWidth
        : MAX_OUTLINE_RESOLUTION / nativeHeight;
    workingWidth = Math.max(1, Math.round(nativeWidth * scale));
    workingHeight = Math.max(1, Math.round(nativeHeight * scale));
  }

  const scaledStroke = strokeWidth * scale;
  const pad = Math.max(0, Math.ceil(scaledStroke));
  const paddedW = workingWidth + 2 * pad;
  const paddedH = workingHeight + 2 * pad;
  // bleed in native image pixels (CSS / crop alignment)
  const bleedPx = scale > 0 ? pad / scale : pad;

  const canvas = document.createElement("canvas");
  canvas.width = paddedW;
  canvas.height = paddedH;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    return null;
  }

  ctx.clearRect(0, 0, paddedW, paddedH);
  ctx.drawImage(
    img,
    0,
    0,
    nativeWidth,
    nativeHeight,
    pad,
    pad,
    workingWidth,
    workingHeight
  );
  const imageData = ctx.getImageData(0, 0, paddedW, paddedH);
  const pixels = imageData.data;

  let subjectAlpha = new Uint8Array(paddedW * paddedH);
  // Match CanvasEditor opaque sensing — ignore near-invisible fringe noise
  const alphaThreshold = Math.max(0, Number(options.alphaThreshold ?? 16));
  for (let i = 0; i < pixels.length; i += 4) {
    subjectAlpha[i / 4] = pixels[i + 3] > alphaThreshold ? 255 : 0;
  }
  // Drop disconnected margin islands (sandbox watermarks) so they are not outlined
  subjectAlpha = keepLargestAlphaComponent(subjectAlpha, paddedW, paddedH);

  const edgeAlpha = new Uint8Array(paddedW * paddedH);
  for (let i = 0; i < edgeAlpha.length; i++) {
    if (subjectAlpha[i] === 0) {
      edgeAlpha[i] = 0;
      continue;
    }
    const x = i % paddedW;
    const y = Math.floor(i / paddedW);
    let isEdge = false;
    for (let dy = -1; dy <= 1 && !isEdge; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || nx >= paddedW || ny < 0 || ny >= paddedH) {
          isEdge = true;
          break;
        }
        if (subjectAlpha[ny * paddedW + nx] === 0) {
          isEdge = true;
          break;
        }
      }
    }
    edgeAlpha[i] = isEdge ? 255 : 0;
  }

  const outlineThickness = dilateAlphaMaskBox(
    edgeAlpha,
    paddedW,
    paddedH,
    scaledStroke
  );

  const color = hexToRgb(options.color || "#000000");
  const outlineImageData = ctx.createImageData(paddedW, paddedH);
  const out = outlineImageData.data;
  for (let i = 0; i < outlineThickness.length; i++) {
    const p = i * 4;
    out[p] = color.r;
    out[p + 1] = color.g;
    out[p + 2] = color.b;
    out[p + 3] = outlineThickness[i];
  }

  ctx.clearRect(0, 0, paddedW, paddedH);
  ctx.putImageData(outlineImageData, 0, 0);
  return {
    dataUrl: canvas.toDataURL("image/png"),
    bleedPx,
  };
}
